import { useCallback, useEffect, useRef, useState } from "react";
import { Camera, Sparkles, Upload } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";

const extractVin = (raw) => {
  let t = (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (t.length === 18 && t.startsWith("I")) t = t.slice(1);
  const m = t.match(/[A-HJ-NPR-Z0-9]{17}/);
  return m ? m[0] : null;
};

export default function VinScannerDialog({ open, onOpenChange, onDetected }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const fileRef = useRef(null);
  const [cameraError, setCameraError] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const finish = useCallback(
    (vin) => {
      toast.success(`VIN captured: ${vin}`);
      onDetected(vin);
      onOpenChange(false);
    },
    [onDetected, onOpenChange]
  );

  useEffect(() => {
    if (!open) return undefined;
    let stopped = false;
    setCameraError(false);
    const hints = new Map();
    hints.set(DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.CODE_39,
      BarcodeFormat.CODE_128,
      BarcodeFormat.DATA_MATRIX,
      BarcodeFormat.QR_CODE,
      BarcodeFormat.PDF_417,
    ]);
    hints.set(DecodeHintType.TRY_HARDER, true);
    const reader = new BrowserMultiFormatReader(hints);
    (async () => {
      try {
        controlsRef.current = await reader.decodeFromConstraints(
          { video: { facingMode: "environment" } },
          videoRef.current,
          (result) => {
            if (stopped || !result) return;
            const vin = extractVin(result.getText());
            if (vin) {
              stopped = true;
              try { controlsRef.current?.stop(); } catch (e) { /* noop */ }
              finish(vin);
            }
          }
        );
      } catch (e) {
        setCameraError(true);
      }
    })();
    return () => {
      stopped = true;
      try { controlsRef.current?.stop(); } catch (e) { /* noop */ }
      controlsRef.current = null;
    };
  }, [open, finish]);

  const aiScan = async (dataUrl) => {
    setAiBusy(true);
    try {
      const r = await api.post("/cars/scan-vin", { image_base64: dataUrl });
      finish(r.data.vin);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't read a VIN — try a closer, sharper shot");
    } finally {
      setAiBusy(false);
    }
  };

  const snapAndScan = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) {
      toast.error("Camera not ready yet — or use 'Upload a photo'");
      return;
    }
    const canvas = document.createElement("canvas");
    const scale = Math.min(1, 1280 / video.videoWidth);
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    aiScan(canvas.toDataURL("image/jpeg", 0.85));
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const scale = Math.min(1, 1280 / img.width);
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      aiScan(canvas.toDataURL("image/jpeg", 0.85));
    };
    img.src = url;
    e.target.value = "";
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="vin-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl text-primary">Scan VIN</DialogTitle>
          <DialogDescription>
            Point the camera at the VIN barcode on the door-jamb sticker — it fills in automatically.
            For a stamped windshield VIN, tap &ldquo;Read with AI&rdquo; while it&rsquo;s in view.
          </DialogDescription>
        </DialogHeader>
        <div className="relative aspect-video bg-black overflow-hidden rounded-sm" data-testid="vin-scanner-video-wrap">
          <video ref={videoRef} className="h-full w-full object-cover" playsInline muted />
          {!cameraError && (
            <div className="pointer-events-none absolute inset-x-8 top-1/2 -translate-y-1/2 h-16 border-2 border-gold/80 rounded-sm" />
          )}
          {cameraError && (
            <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-sm text-white/80" data-testid="vin-camera-error">
              Camera unavailable or permission denied. Use &ldquo;Upload a photo&rdquo; below instead.
            </div>
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            onClick={snapAndScan}
            disabled={aiBusy || cameraError}
            className="rounded-sm gap-1 bg-accent text-accent-foreground hover:bg-accent/90 flex-1"
            data-testid="vin-ai-snap"
          >
            <Sparkles className="h-3 w-3" /> {aiBusy ? "Reading…" : "Read with AI (snap photo)"}
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => fileRef.current?.click()}
            disabled={aiBusy}
            className="rounded-sm gap-1 flex-1"
            data-testid="vin-upload-photo"
          >
            <Upload className="h-3 w-3" /> Upload a photo
          </Button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={onFile}
            className="hidden"
            data-testid="vin-file-input"
          />
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1">
          <Camera className="h-3 w-3" /> Tip: the barcode scanner works best in good light, 6&ndash;10 inches from the sticker.
        </p>
      </DialogContent>
    </Dialog>
  );
}
